<!--
Referencing the implementation of cv-modal from "@carbon/vue/src/components/cv-modal"

Specs for responsive guidelines:
Mobile View:
1) The component will take up the whole screen estate without displaying the App header

Tablet Landscape / Desktop View:
1) Up to 2 columns can be rendered by passing the content into the respective slots.

-->

<template>
  <div
    class="content-subpage bx--modal"
    :class="[
      {
        'is-visible': visible,
        'light-theme': isStudent,
        'has-content-sticky': hasContentSticky,
        'has-sticky-header': headerSticky,
        _condensed: condensed,
      },
      size,
    ]"
  >
    <div class="bx--modal-container">
      <div
        class="header"
        :class="{
          'has-buttons': hasHeaderButtons,
          'has-overflow-menu-button': hasHeaderOverflowMenu,
        }"
      >
        <div
          class="breadcrumb"
          :class="{
            singular: breadcrumb.length === 1,
          }"
        >
          <template v-if="isMiddleTruncated">
            <ellipsis-text class="title" :text="breadcrumb[0]" />
            <svg-icon name="ForwardSlash16" kind="indicative" />
            <span class="ellipsis" />
            <svg-icon name="ForwardSlash16" kind="indicative" />
            <ellipsis-text class="title" :text="breadcrumb[1]" />
            <svg-icon name="ForwardSlash16" kind="indicative" />
            <ellipsis-text class="title" :text="breadcrumb[2]" />
          </template>
          <template v-for="(item, index) in breadcrumb" v-else :key="item + index">
            <ellipsis-text class="title" :text="item" />
            <svg-icon
              v-if="index !== breadcrumb.length - 1"
              :key="index"
              name="ForwardSlash16"
              kind="indicative"
            />
          </template>
          <div v-if="hasTitleButtons" class="title-buttons">
            <slot name="title-buttons" />
          </div>
        </div>
        <!-- Currently supports only 1 button as max-width for breadcrumb is static via CSS.
             For support of more than 1 buttons in the future, need to dynamically calculate
             max-width based on number of buttons via JS instead-->
        <cv-button-set v-if="hasHeaderButtons">
          <slot name="header-buttons" />
        </cv-button-set>
        <span class="close-wrapper">
          <svg-icon name="Close24" kind="action" class="btn-close" @click="actionClose" />
        </span>
      </div>

      <div
        class="content"
        :class="{
          'has-action-button': hasActionButton,
          'has-sub-content': hasSubContent,
          'has-banner': hasBanner,
        }"
      >
        <overlay-loader v-if="isProcessing" />
        <div v-if="hasBanner" class="subpage-banner"><slot name="banner" /></div>
        <div v-if="isLoading" class="main">
          <inline-loader />
        </div>

        <div v-else class="main">
          <icon-link
            v-if="hasParentSubpage"
            icon="ArrowLeft24"
            :label="backLabel"
            @clicked="actionBack"
          />
          <div v-if="showContentTitle" class="main-header">
            <span v-if="titleIcon" class="icon">
              <svg-icon class="title-icon" :name="titleIcon" kind="indicative" />
            </span>
            <h4 class="title">{{ title }}</h4>
            <cv-interactive-tooltip
              v-if="showTooltip"
              :direction="tooltipDirection"
              class="field-tooltip"
            >
              <template #trigger>
                <svg-icon :name="tooltipIcon" :kind="tooltipKind" />
              </template>
              <template #content>
                <p>{{ tooltipMessage }}</p>
              </template>
            </cv-interactive-tooltip>
            <span class="title-link">
              <slot name="title-link" />
            </span>
          </div>
          <slot name="main-content" />
          <div v-if="condensed" class="content-bg-wrapper">
            <slot name="main-content-bg"></slot>
          </div>
        </div>
        <div v-if="hasSubContent" class="sub">
          <slot name="sub-content" />
        </div>
        <!-- Content subpage should only contain one action button -->
        <div v-if="hasActionButton" class="action-button">
          <slot name="action-button" />
        </div>
      </div>

      <slot name="content-sticky" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { nextTick, onBeforeUnmount, useSlots } from "vue";
import { ref, computed, watch, onMounted, onBeforeUpdate } from "vue";
import { useVm } from "@utils/vm.ts";
import { useStore } from "vuex";

// Props
interface Props {
  visible?: boolean;
  titles?: unknown[];
  showContentTitle?: boolean;
  showTooltip?: boolean;
  tooltipMessage?: string | null;
  contentTitle?: string;
  titleIcon?: string;
  hasParentSubpage?: boolean;
  isProcessing?: boolean;
  isLoading?: boolean;
  backLabel?: string;
  size?: string;
  condensed?: boolean;
  headerSticky?: boolean;
  tooltipIcon?: string;
  tooltipKind?: string;
  tooltipDirection?: string;
}

const {
  visible = false,
  titles = [],
  showContentTitle = true,
  showTooltip = false,
  tooltipMessage = null,
  contentTitle = "",
  titleIcon = "",
  hasParentSubpage = false,
  isProcessing = false,
  isLoading = false,
  condensed = false,
  headerSticky = false,
  backLabel = "",
  size = "large",
  tooltipIcon = "Info24",
  tooltipKind = "action",
  tooltipDirection = "top",
} = defineProps<Props>();

const emit = defineEmits<{
  "content-subpage-hidden": [];
  "content-subpage-back": [];
}>();

// Composables
const { vm, vmExpose } = useVm();
const store = useStore();
const slots = useSlots();

// Data
const isMiddleTruncated = ref(false);
const hasActionButton = ref(false);
const hasHeaderOverflowMenu = ref(false);
const hasSubContent = ref(false);
const hasBanner = ref(false);
const hasHeaderButtons = ref(false);
const hasTitleButtons = ref(false);
const dataTitles = ref([]);
const contentHeightSettingInterval = ref<number | null>(null);

// Computed
const isStudent = computed<boolean>(() => store.getters["auth/isStudent"]);

const breadcrumb = computed(() => {
  let breadcrumbArray = [...dataTitles.value];

  if (breadcrumbArray.length > 3) {
    breadcrumbArray = [breadcrumbArray[0], ...breadcrumbArray.slice(-2)];
    setMiddleTruncation(true);
    return breadcrumbArray;
  }

  setMiddleTruncation(false);
  return breadcrumbArray;
});

const title = computed(() => {
  if (contentTitle) return contentTitle;
  return breadcrumb.value ? breadcrumb.value[breadcrumb.value.length - 1] : "";
});

const hasContentSticky = computed(() => {
  return !!slots["content-sticky"];
});

// Methods
const actionClose = () => {
  emit("content-subpage-hidden");
};

const actionBack = () => {
  emit("content-subpage-back");
};

const actionShown = () => {
  // intentionally left blank
};

const actionHidden = () => {
  // intentionally left blank
};

const setMiddleTruncation = (value) => {
  isMiddleTruncated.value = value;
};

const checkSlots = () => {
  nextTick(() => {
    hasActionButton.value = !!slots["action-button"];
    hasSubContent.value = !!slots["sub-content"];
    hasHeaderButtons.value = !!slots["header-buttons"];
    hasTitleButtons.value = !!slots["title-buttons"];
    hasBanner.value = !!slots["banner"];
  });
};

const updateContentSubpageHeightCSSVariable = () => {
  const contentSubpage = vm.$el;
  if (contentSubpage.nodeType === Node.ELEMENT_NODE) {
    const modalContainer = DOMUtils.elOf(contentSubpage, ".bx--modal-container")[0];
    const height = modalContainer.clientHeight;
    DOMUtils.setCSSVariableInElement(contentSubpage, "--content-subpage-height", `${height}px`);
  }
};

const checkHeaderOverflowMenu = () => {
  const contentSubpage = vm.$el;
  if (contentSubpage.nodeType === Node.ELEMENT_NODE) {
    const overflowMenu = DOMUtils.elOf(contentSubpage, ".header .overflow-menu-button");
    hasHeaderOverflowMenu.value = overflowMenu && overflowMenu.length > 0;
  }
};

// Watch
watch(
  () => visible,
  (val) => {
    if (val) {
      actionShown();
    } else {
      actionHidden();
    }
  },
);

watch(
  () => titles,
  (newTitles) => {
    dataTitles.value = [...newTitles];
  },
  { immediate: true, deep: true },
);

// Mounted
onMounted(() => {
  if (visible) {
    actionShown();
  }
  checkSlots();

  setTimeout(checkHeaderOverflowMenu, 500);

  // Poll for content subpage to be loaded fully first before calculating its height
  contentHeightSettingInterval.value = setInterval(() => {
    if (!isLoading) {
      updateContentSubpageHeightCSSVariable();
      clearInterval(contentHeightSettingInterval.value);
    }
  }, 500);
});

// BeforeUpdate
onBeforeUpdate(() => {
  checkSlots();
});

onBeforeUnmount(() => {
  if (contentHeightSettingInterval.value) {
    clearInterval(contentHeightSettingInterval.value);
    contentHeightSettingInterval.value = null;
  }
});

vmExpose({
  actionClose,
  actionBack,
  actionShown,
  actionHidden,
  updateContentSubpageHeightCSSVariable,
});
</script>

<style lang="scss">
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/abstracts/variables" as *;

.bx--modal.content-subpage {
  &.light-theme {
    .bx--modal-container {
      > .header {
        background-color: $ui-01;
        color: $text-01;

        .sls-icon {
          color: $interactive-01;
        }

        .bx--btn--primary {
          .sls-icon {
            color: $ui-01;
          }
        }

        ._completed {
          .bx--btn--primary {
            .sls-icon {
              color: $interactive-01;
            }
          }
        }

        .button {
          color: $interactive-01;

          &.close {
            &:hover {
              [stroke] {
                stroke: $interactive-01;
              }
            }
          }
        }

        .star-resource-button {
          .sls-icon {
            [stroke] {
              stroke: $interactive-01;
            }

            [fill] {
              &:not([fill="#FBBC04"]) {
                fill: $interactive-01;
              }
            }
          }
        }
      }
    }
  }

  // E.g AttemptBulkFeedbackContentSubpage
  &.small {
    .bx--modal-container {
      @include media("tablet-min") {
        width: $content-subpage-tablet-width;
        min-height: 420px;
      }

      @include media("desktop") {
        width: $content-subpage-desktop-width;
      }

      @include media("height-md") {
        min-height: auto;
      }
    }
  }

  // Loading state for content subpage when data is processing
  &.loading-state {
    .bx--modal-container {
      > .header {
        // disabled close btn upon loading state on content subpage
        .close-wrapper {
          cursor: not-allowed;

          .btn-close {
            pointer-events: none;
          }
        }
      }
    }
  }

  &._condensed {
    .icon-link {
      margin: $spacing-05 0 0 $spacing-05;
    }
    .bx--modal-container {
      .content {
        .main {
          padding: 0;
          .main-header {
            padding: $spacing-05 $spacing-05 0 $spacing-05;
            margin: 0;
          }
          > .search-bar-wrapper {
            padding: $spacing-05;
            background-color: $ui-background;
          }
        }
      }
    }
  }

  &.has-sticky-header {
    .search-bar-wrapper {
      @include media("desktop") {
        position: sticky;
        top: 0;
        z-index: calc(z-map("default") + 3);
      }
    }
  }

  .content-bg-wrapper {
    padding: $spacing-05;
    background-color: $ui-01;
  }

  .subpage-banner {
    @include carbon--type-style("body-short-01", true);
    min-height: $global-banner-height;
    height: $global-banner-height;
    width: 100%;
    position: sticky;
    display: flex;
    align-items: center;
    gap: $spacing-02;
    padding: 0 $spacing-05;
    top: 0;
    z-index: z-map("default");
    background-color: $status-aggregated;

    .bx--link {
      @include carbon--type-style("button-02", true);
      margin-left: auto;
    }

    .bx--assistive-text {
      white-space: break-spaces;
    }

    .sls-icon {
      path {
        fill: $interactive-01;
      }
    }
  }

  .bx--modal-container {
    grid-template-rows: 3rem 1fr;

    @include media("mobile") {
      height: calc(
        (var(--vh, 1vh) * 100)
      ); // fallback to 100vh for browsers that do not support 100dvh
      height: 100dvh;
      width: 100vw;
      border-radius: 0;
    }

    @include media("tablet") {
      width: $content-subpage-tablet-width;
    }

    @include media("tablet-min") {
      min-height: 420px;
      max-height: calc((var(--vh, 1vh) * 100) - #{$spacing-07 * 2});
      max-height: calc(100dvh - #{$spacing-07 * 2});
      border-radius: 10px;
    }

    @include media("desktop") {
      width: $content-subpage-desktop-width;
    }

    @include media("height-md") {
      min-height: auto;
    }

    > .header {
      display: flex;
      align-items: center;
      padding-right: $spacing-05;
      background-color: $inverse-02;
      color: $ui-01;
      width: inherit;
      border-bottom: 1px solid $ui-03;

      @include media("tablet-min") {
        border-radius: 10px 10px 0 0;
      }

      &.has-buttons {
        grid-template-columns: 1fr 3.5rem auto;

        .bx--btn-set {
          justify-content: flex-end;
          margin-left: (-$spacing-03);
          margin-right: $spacing-03;
        }
      }

      &.has-overflow-menu-button {
        grid-template-columns: 1fr 9.5rem auto;
      }

      .close-wrapper {
        display: inline-flex;
      }

      .btn-close {
        cursor: pointer;
        color: $inverse-link;

        path {
          stroke: currentColor;
        }
      }

      .breadcrumb {
        @include carbon--type-style("body-short-01", true);
        display: flex;
        align-items: center;
        flex: 1;
        max-width: 100%;
        padding: 0 $spacing-05;
        word-break: break-word;
        overflow: hidden;

        &.singular {
          .title {
            max-width: 100%;
          }
        }

        .ellipsis {
          flex: 0 0 auto;
          color: $text-03;

          &::before {
            content: " ... ";
          }
        }

        .title {
          position: relative;
          width: auto;
          max-width: calc(50% - 1rem);
          margin-bottom: 0;

          &:not(:last-of-type) {
            color: $text-03;
          }
        }

        .sls-icon {
          flex: 0 0 auto;
          color: $inverse-link;
        }
      }

      .button {
        cursor: pointer;
        color: $inverse-link;

        path {
          fill: currentColor;
        }
      }

      .bx--btn {
        border: 1px solid transparent;

        &.bx--btn--icon-only {
          width: 2.5rem;
          height: 2.5rem;
        }

        &:focus {
          box-shadow: none;
          border: 1px solid $focus;
        }
      }

      .fetch-data-button {
        &._completed {
          .bx--btn {
            background-color: tint($interactive-01, 80%);
            border: 2px solid $interactive-01;
            color: $interactive-01;
          }
        }

        .message-component {
          z-index: z-map("content-subpapge-header-tooltip");
        }
      }

      .title-buttons {
        margin-left: $spacing-03;
      }
    }

    > .content {
      width: inherit;
      overflow-y: auto;
      position: relative;
      border-radius: 10px;

      @include media("desktop") {
        display: flex;
        flex-flow: row wrap;
        overflow-y: hidden;

        > :is(.main, .sub) {
          position: relative;
          flex: 1 1 50%;
          max-height: 100%;
          overflow-y: auto;
        }

        // An auto-sized banner row prevents wrapped translations from reducing
        // the space available to the scrollable content panels.
        &.has-banner {
          &.has-sub-content {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          > .subpage-banner {
            grid-column: 1 / -1;
          }
        }
      }

      /* Additional bottom padding to prevent the action-button from overlapping the content
       * For desktop, the bottom padding is added to the main and sub-content instead as
       * the scrollbar should appear for the two panels instead of the whole content
      */
      &.has-action-button {
        @include media("mobile") {
          padding-bottom: $layout-06;
        }

        // Padding-bottom is lesser for tablet compared to mobile/desktop as position of
        // FAB is sticky relative to the content and not absolute/fixed
        @include media("tablet") {
          padding-bottom: $spacing-05;

          // Padding is added to last-child of main or sub-content to address issue in Safari
          // whereby it's not pushing down while datatable is still loading
          > .main {
            // Min-height to ensure the FAB is positioned at the bottom even when
            // there is insufficient content
            min-height: 19rem;

            > div:last-child {
              padding-bottom: $layout-03;
            }
          }

          &.has-sub-content {
            > :is(.main, .sub) {
              min-height: 9rem;
            }

            > .main {
              > div:last-child {
                padding-bottom: 0;
              }
            }

            > .sub {
              > div:last-child {
                padding-bottom: $layout-03;
              }
            }
          }
        }

        @include media("desktop") {
          padding-bottom: 0;

          // Padding is added to last-child of main or sub-content to address issue in Safari
          // whereby it's not pushing down while datatable is still loading
          > .main {
            > :is(div, .bx--form):last-child {
              padding-bottom: $layout-06;
            }
          }

          &.has-sub-content {
            > .main {
              > :is(div, .bx--form):last-child {
                padding-bottom: 0;
              }
            }

            > .sub {
              > :is(div, .bx--form):last-child {
                padding-bottom: $layout-06;
              }
            }
          }
        }
      }

      > :is(.main, .sub) {
        padding: $spacing-05;
      }

      > .sub {
        position: relative;
        background-color: $ui-01;
      }
    }
  }

  .has-action-button {
    .bx--list-box__menu {
      z-index: z-map("dropdown-in-subpage");
    }
  }

  .star-resource-button {
    .sls-icon {
      [stroke] {
        stroke: $inverse-link;
      }

      [fill] {
        &:not([fill="#FBBC04"]) {
          fill: $inverse-link;
        }
      }
    }
  }

  .media-component {
    .star-resource-button {
      .sls-icon {
        [fill] {
          &:not([fill="#FBBC04"]) {
            fill: $icon-02;
          }
        }
      }
    }
  }

  .action-button {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    padding: 0;
    cursor: pointer;
    z-index: z-map("functional-action-button");

    @include media("mobile") {
      position: fixed;
    }

    @include media("tablet") {
      position: sticky;
      float: right;
      bottom: 0;
    }

    .bx--btn,
    .fetch-data-button .bx--btn {
      @include carbon--type-style("button-02");
      display: flex;
      flex-flow: column;
      min-width: 68px;
      height: 68px;
      max-height: 68px;
      padding: 0;
      text-align: center;
      border-radius: 50%;
      box-shadow: $shadow-03;

      &[disabled] {
        opacity: 1;
        box-shadow: $shadow-02;
      }

      .sls-icon {
        margin-right: 0;

        path {
          &[fill] {
            fill: currentColor;
          }

          &[stroke] {
            stroke: currentColor;
          }
        }
      }
    }
  }

  .main-header {
    display: flex;
    align-items: center;
    margin-bottom: $spacing-05;

    > .title {
      @include carbon--type-style("productive-heading-02", true);
      word-break: break-word;
    }

    .icon {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 2.5rem;
      height: 2.5rem;
      background-color: $ui-01;
      border-radius: 50%;
      padding: $spacing-02;
      margin-right: $spacing-03;
      color: $interactive-01;
    }

    .field-tooltip {
      display: flex;
      align-items: center;
    }

    .bx--tooltip__trigger {
      .sls-icon {
        fill: none;

        [stroke] {
          stroke: $interactive-03;
        }

        [fill] {
          fill: $interactive-03;
        }
      }
    }

    .title-link {
      margin-left: $spacing-03;
      width: 24px;
      height: 24px;

      .sls-icon {
        [fill] {
          fill: $interactive-01;
        }
      }
    }
  }

  // TinyMCE
  .rich-text-editor {
    .mce-content-body.mce-edit-focus {
      overflow-y: scroll;
    }

    .tox {
      .tox-menu {
        &.tox-collection {
          &.tox-collection--list {
            // to fix language dropdown list height within content subpage
            max-height: 260px !important;
          }
        }
      }
    }
  }
}

// Fix emoji popup being hidden inside subpage sub content in Safari
// target specifically only when tinymce popup is opened
body {
  &.tox-dialog__disable-scroll {
    .content-subpage {
      .bx--modal-container {
        > .content {
          > .sub {
            overflow: visible;
          }
        }
      }
    }
  }
}
</style>
